import { useEffect, useId, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './Button'

interface ModalProps {
  abierto: boolean
  onCerrar: () => void
  titulo?: string
  children: React.ReactNode
  tamano?: 'sm' | 'md' | 'lg' | 'xl'
  sinCerrarAlClickFuera?: boolean
}

export function Modal({
  abierto,
  onCerrar,
  titulo,
  children,
  tamano = 'md',
  sinCerrarAlClickFuera = false,
}: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const ultimoElementoEnFocoRef = useRef<HTMLElement | null>(null)
  const titleId = useId()

  // Cerrar con Escape
  useEffect(() => {
    if (!abierto) return
    const manejar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar()
    }
    document.addEventListener('keydown', manejar)
    return () => document.removeEventListener('keydown', manejar)
  }, [abierto, onCerrar])

  useEffect(() => {
    if (abierto) {
      ultimoElementoEnFocoRef.current = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
      const firstFocusable = getFocusableElements(dialogRef.current)[0]
      const focusTarget = firstFocusable ?? dialogRef.current
      focusTarget?.focus()
      return () => restaurarFoco(ultimoElementoEnFocoRef.current)
    }

    restaurarFoco(ultimoElementoEnFocoRef.current)
  }, [abierto])

  // Prevenir scroll del body
  useEffect(() => {
    if (abierto) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => { document.body.style.overflow = '' }
  }, [abierto])

  if (!abierto) return null

  const tamanos = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-2xl',
  }

  const manejarTeclado = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return

    const focusableElements = getFocusableElements(dialogRef.current)
    if (focusableElements.length === 0) {
      event.preventDefault()
      dialogRef.current?.focus()
      return
    }

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={(e) => {
        if (!sinCerrarAlClickFuera && e.target === overlayRef.current) onCerrar()
      }}
    >
      <div
        ref={dialogRef}
        className={cn(
          'relative w-full rounded-lg border border-slate-200 bg-white text-slate-950 shadow-2xl animate-slide-up dark:border-white/[0.08] dark:bg-[#0f1520] dark:text-white',
          tamanos[tamano]
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titulo ? titleId : undefined}
        aria-label={titulo ? undefined : 'Diálogo'}
        tabIndex={-1}
        onKeyDown={manejarTeclado}
      >
        {titulo && (
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5 dark:border-white/[0.06]">
            <h2 id={titleId} className="text-[14px] font-semibold text-slate-950 dark:text-white/90">{titulo}</h2>
            <Button
              variante="ghost"
              tamano="sm"
              onClick={onCerrar}
              aria-label="Cerrar"
              className="-mr-1 p-1.5 text-slate-700 hover:bg-slate-100 hover:text-slate-950 dark:text-white/65 dark:hover:bg-white/[0.05] dark:hover:text-white"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}

function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (!container) return []
  return Array.from(container.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )).filter(element => !element.hasAttribute('hidden'))
}

function restaurarFoco(elemento: HTMLElement | null): void {
  if (elemento?.isConnected) elemento.focus()
}
